import { useEffect, useMemo, useRef } from 'react';

/**
 * Rotación de las filas de plantillas de la home — ADR-2026-07-31-plantillas-home-rotan-tres.
 *
 * La home no muestra todas las categorías: muestra `cantidad` (3), y en cada entrada
 * o refresh muestra **otras**. Con 10 categorías activas en la base, listarlas todas
 * convierte la home en un scroll interminable donde nada destaca; mostrar siempre las
 * mismas 3 esconde el resto del catálogo para siempre.
 *
 * Es la misma idea que ya tenía el preview (`elegirFilasPlantillas` de
 * `contenidoAtlas`, ADR-2026-07-30-plantillas-minimal-10-categorias-rotando), pero
 * genérica: aquélla estaba atada al catálogo estático y ahora las filas son las
 * colecciones reales que trae el backend.
 *
 * Dos cosas que la versión vieja no tenía que resolver y ésta sí:
 *
 * 1. **Las filas llegan async.** `fetchTemplatesV2` y las poses de e-commerce
 *    resuelven por separado, así que la lista crece durante el primer segundo. Con un
 *    sorteo "una sola vez al montar" se elegiría sobre una lista incompleta. Por eso
 *    el sorteo es determinístico dada una semilla fija por montaje: mientras llegan
 *    datos la elección se recalcula, pero no "baraja de nuevo" en cada render ni ante
 *    cualquier interacción de la pantalla.
 * 2. **Hay que registrar lo mostrado, no lo sorteado.** El registro va en un efecto
 *    sobre el resultado final, así el "no repetir" de la próxima visita compara
 *    contra lo que el usuario efectivamente vio.
 */

/** Categorías mostradas en la visita anterior, para no repetirlas. */
const CLAVE_ROTACION = 'atlas:plantillas-home-vistas';

function leerPrevias(): string[] {
  try {
    const raw = localStorage.getItem(CLAVE_ROTACION);
    const parsed = raw ? JSON.parse(raw) : null;
    return Array.isArray(parsed) ? parsed.filter((x) => typeof x === 'string') : [];
  } catch {
    return [];
  }
}

/**
 * PRNG con semilla (mulberry32). `Math.random()` no sirve acá: se necesita que el
 * mismo montaje, con la misma lista, dé el mismo resultado — si no, cada render
 * devolvería otras 3 filas.
 */
function prng(semilla: number): () => number {
  let a = semilla >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export function useRotacionPlantillas<T extends { id: string }>(
  filas: T[],
  cantidad = 3,
): T[] {
  // Una semilla por montaje: entrar a la home o refrescar da una tanda distinta;
  // moverse dentro de la home (abrir un sheet, scrollear) no.
  const semilla = useRef(Math.floor(Math.random() * 2 ** 31)).current;

  // Las previas se leen UNA vez, al montar. Si se leyeran en el memo, el efecto que
  // guarda abajo las pisaría y el propio render siguiente se auto-excluiría.
  const previas = useRef<string[] | null>(null);
  if (previas.current === null) previas.current = leerPrevias();

  // Clave del memo: la lista de ids. Mientras llegan datos cambia y la elección se
  // recalcula; una vez estable, no se vuelve a tocar.
  const ids = filas.map((f) => f.id).join('|');

  const elegidas = useMemo(() => {
    if (filas.length <= cantidad) return filas;

    // Pozo sin las de la visita anterior. Si excluir dejara menos de las que hay que
    // mostrar, se usa la lista entera: es preferible repetir una categoría a mostrar
    // la sección corta.
    const candidatas = filas.filter((f) => !previas.current!.includes(f.id));
    const pozo = candidatas.length >= cantidad ? candidatas : filas;

    // Fisher-Yates con la semilla del montaje. Sobre una copia: no se toca el orden
    // de las colecciones, que es el que definió la categoría en la base.
    const azar = prng(semilla);
    const mezclado = [...pozo];
    for (let i = mezclado.length - 1; i > 0; i--) {
      const j = Math.floor(azar() * (i + 1));
      [mezclado[i], mezclado[j]] = [mezclado[j], mezclado[i]];
    }
    return mezclado.slice(0, cantidad);
    // `ids` es la dependencia real; `filas` cambia de identidad en cada render del
    // padre aunque su contenido sea el mismo.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ids, cantidad, semilla]);

  // Registrar lo que se está mostrando, para que la próxima visita lo excluya.
  useEffect(() => {
    if (elegidas.length === 0) return;
    try {
      localStorage.setItem(CLAVE_ROTACION, JSON.stringify(elegidas.map((f) => f.id)));
    } catch {
      /* sin storage, la próxima visita elige sin exclusión */
    }
  }, [elegidas]);

  return elegidas;
}

export default useRotacionPlantillas;
